import { CloudArrowUpIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { ChangeEvent, DragEvent, useCallback, useMemo, useState } from 'react';

import { Text } from '../ui/catalyst/text';

interface DragDropFileInputProps {
  selectedFile: File | null;
  onFileChange: (file: File | null) => void;
  className?: string;
  /** HTML input accept string. Defaults to PDF. */
  accept?: string;
  /** Optional validator override. Return true to accept the file. */
  validateFile?: (file: File) => boolean;
  /** Title text when idle (no drag). */
  idleTitle?: string;
  /** Title text when dragging over. */
  draggingTitle?: string;
  /** Subtitle/help text shown below. */
  subtitle?: string;
  /** Allow multiple file selection; default false (uses first file). */
  allowMultiple?: boolean;
}

export const DragDropFileInput = ({
  selectedFile,
  onFileChange,
  className = '',
  accept = '.pdf,application/pdf',
  validateFile,
  idleTitle = 'Upload PDF file',
  draggingTitle = 'Drop your PDF here',
  subtitle = 'Drag and drop a PDF file here, or click to browse',
  allowMultiple = false,
}: DragDropFileInputProps) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const acceptedList = useMemo(() => {
    return accept
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
  }, [accept]);

  const isFileAllowed = useCallback(
    (file: File): boolean => {
      if (validateFile) return validateFile(file);
      const name = file.name.toLowerCase();
      const type = (file.type || '').toLowerCase();
      const hasMimeMatch = acceptedList.some(
        (a) => a.includes('/') && a === type,
      );
      const hasExtMatch = acceptedList.some(
        (a) => a.startsWith('.') && name.endsWith(a),
      );
      // If accept is empty, allow all
      const acceptsAll = acceptedList.length === 0;
      return acceptsAll || hasMimeMatch || hasExtMatch;
    },
    [acceptedList, validateFile],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);

      const { files } = e.dataTransfer;
      if (!files || files.length === 0) return;
      const file = files[0];
      if (isFileAllowed(file)) {
        onFileChange(file);
      }
    },
    [isFileAllowed, onFileChange],
  );

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (!files || files.length === 0) return;
      const file = files[0];
      if (isFileAllowed(file)) {
        onFileChange(file);
      }
    },
    [isFileAllowed, onFileChange],
  );

  const removeFile = useCallback(() => {
    onFileChange(null);
  }, [onFileChange]);

  return (
    <div className={className}>
      {selectedFile ? (
        // File selected state
        <div className="border-2 border-green-200 bg-green-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DocumentIcon className="h-8 w-8 text-green-600" />
              <div>
                <Text className="font-medium text-green-900">
                  {selectedFile.name}
                </Text>
                <Text className="text-sm text-green-700">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Text>
              </div>
            </div>
            <button
              type="button"
              onClick={removeFile}
              className="text-green-600 hover:text-green-800 font-medium text-sm"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        // Drag and drop area
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
            isDragOver
              ? 'border-blue-400 bg-blue-50'
              : 'border-zinc-300 bg-zinc-50 hover:border-zinc-400 hover:bg-zinc-100'
          }`}
        >
          <input
            type="file"
            accept={accept}
            multiple={allowMultiple}
            onChange={handleFileSelect}
            className="hidden"
            id="file-input"
          />
          <label htmlFor="file-input" className="cursor-pointer block">
            <CloudArrowUpIcon className="h-12 w-12 text-zinc-400 mx-auto mb-4" />
            <Text className="text-lg font-medium text-zinc-900 mb-2">
              {isDragOver ? draggingTitle : idleTitle}
            </Text>
            <Text className="text-sm text-zinc-500">{subtitle}</Text>
          </label>
        </div>
      )}
    </div>
  );
};
